import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { PaginationState, RowSelectionState } from "@tanstack/react-table";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import {
  Box,
  Flex,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { useHistory } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { deleteBacktest, listBacktests } from "../../../services/backtestsApi";
import { getReport, listReports } from "../../../services/reportsApi";
import type { Backtest } from "../../../types/backtests";
import type { Report } from "../../../types/reports";
import { getReportDisplayTitle } from "../../../utils/reportWindowLabel";
import { getReportTypeDisplayLabel } from "../../../utils/reportTypeRules";
import { SimulatorAlert } from "./SimulatorAlert";
import { NeedHelpButton } from "@/components/need-help-button";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { SimulatorListActionBar } from "./SimulatorListActionBar";
import { SimulatorListTable } from "./SimulatorListTable";
import type { SimulatorRunOriginFilter } from "./simulatorListTypes";
import {
  SIMULATOR_NEW_PATH,
  simulatorResultsWithBacktestPath,
} from "./simulatorRoutes";

const FETCH_LIMIT = 100;
const DEFAULT_PAGE_SIZE = 25;

/** Same shell as Reports list card — outer stroke + inner table grid use #d3d3d3 (light) / --input (dark). */
const SIMULATOR_LIST_CARD_CLASS = cn(
  "flex w-full min-w-0 flex-col gap-4 overflow-x-auto rounded-[6px] bg-card p-4 text-card-foreground shadow-none",
  "dark:bg-sidebar dark:text-sidebar-foreground",
  "xl:overflow-x-hidden",
);

function filterByOrigin(rows: Backtest[], origin: SimulatorRunOriginFilter): Backtest[] {
  if (origin === "all") return rows;
  if (origin === "standalone") return rows.filter((b) => !b.report_id);
  return rows.filter((b) => Boolean(b.report_id));
}

function matchesSimulatorSearch(
  backtest: Backtest,
  query: string,
  reportTitlesById: Record<string, string>,
  reportTypesById: Record<string, string>,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const title = (backtest.title?.trim() || "Untitled simulation").toLowerCase();
  const id = backtest.id.toLowerCase();
  const account = (backtest.broker_account_code ?? "").toLowerCase();
  const origin = backtest.report_id ? "report" : "simulator";
  const originLabel = backtest.report_id ? "from reports" : "from simulator";

  const reportTitle = backtest.report_id
    ? (reportTitlesById[backtest.report_id] ?? "Unknown report").toLowerCase()
    : "";
  const reportType = backtest.report_id
    ? getReportTypeDisplayLabel(reportTypesById[backtest.report_id]).toLowerCase()
    : "";

  let created = "";
  if (backtest.created_at) {
    const d = new Date(backtest.created_at);
    if (!Number.isNaN(d.getTime())) {
      created = d
        .toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
        .toLowerCase();
    }
  }

  return (
    title.includes(q) ||
    id.includes(q) ||
    account.includes(q) ||
    origin.includes(q) ||
    originLabel.includes(q) ||
    reportTitle.includes(q) ||
    reportType.includes(q) ||
    created.includes(q)
  );
}

async function resolveReportMetadataById(
  reportIds: string[]
): Promise<{ titlesById: Record<string, string>; typesById: Record<string, string> }> {
  if (reportIds.length === 0) {
    return { titlesById: {}, typesById: {} };
  }

  const titlesById: Record<string, string> = {};
  const typesById: Record<string, string> = {};

  const ingest = (report: Report) => {
    titlesById[report.id] = getReportDisplayTitle(report);
    if (report.report_type) {
      typesById[report.id] = report.report_type;
    }
  };

  try {
    const resp = await listReports({
      limit: 500,
      offset: 0,
    });
    for (const report of resp.reports ?? []) {
      ingest(report);
    }
  } catch {
    // Fall through to per-id fetch below.
  }

  const missing = reportIds.filter((id) => !titlesById[id]);
  await Promise.all(
    missing.map(async (id) => {
      try {
        const report = await getReport(id);
        ingest(report);
      } catch {
        // Leave unresolved; table falls back to generic labels.
      }
    })
  );

  return { titlesById, typesById };
}

export default function SimulatorListPage(): JSX.Element {
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const history = useHistory();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const { activeAccountCode } = useActiveBrokerAccount();

  const [originFilter, setOriginFilter] = useState<SimulatorRunOriginFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Backtest[]>([]);
  const [reportTitlesById, setReportTitlesById] = useState<Record<string, string>>({});
  const [reportTypesById, setReportTypesById] = useState<Record<string, string>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectAllFromHeader, setSelectAllFromHeader] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>(() => {
    const saved = localStorage.getItem("simulatorList_pageSize");
    const pageSize = saved ? Number(saved) : DEFAULT_PAGE_SIZE;
    return { pageIndex: 0, pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE };
  });

  const load = useCallback(async () => {
    setListLoading(true);
    setError(null);
    try {
      const resp = await listBacktests({
        status: "saved",
        limit: FETCH_LIMIT,
        offset: 0,
        include_result: false,
        ...(activeAccountCode ? { broker_account_code: activeAccountCode } : {}),
      });
      const backtests = Array.isArray(resp?.backtests) ? resp.backtests : [];
      setRows(backtests);

      const reportIds = Array.from(
        new Set(
          backtests
            .map((b) => b.report_id)
            .filter((id): id is string => typeof id === "string" && id.trim().length > 0)
        )
      );
      const { titlesById, typesById } = await resolveReportMetadataById(reportIds);
      setReportTitlesById(titlesById);
      setReportTypesById(typesById);
    } catch (e) {
      setRows([]);
      setReportTitlesById({});
      setReportTypesById({});
      setError(e instanceof Error ? e.message : "Failed to load simulations.");
    } finally {
      setListLoading(false);
    }
  }, [activeAccountCode]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setRowSelection({});
    setSelectAllFromHeader(false);
  }, [originFilter, searchQuery]);

  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllFromHeader(false);
    }
  }, [rowSelection]);

  useEffect(() => {
    setSelectAllFromHeader(false);
  }, [pagination.pageIndex]);

  const searchQueryTrimmed = searchQuery.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    const byOrigin = filterByOrigin(rows, originFilter);
    if (!searchQueryTrimmed) return byOrigin;
    return byOrigin.filter((row) =>
      matchesSimulatorSearch(row, searchQueryTrimmed, reportTitlesById, reportTypesById),
    );
  }, [rows, originFilter, searchQueryTrimmed, reportTitlesById, reportTypesById]);

  const pageCount =
    filteredRows.length === 0 ? 0 : Math.ceil(filteredRows.length / pagination.pageSize);

  useEffect(() => {
    localStorage.setItem("simulatorList_pageSize", String(pagination.pageSize));
  }, [pagination.pageSize]);

  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex >= pageCount) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [pageCount, pagination.pageIndex]);

  const openRun = useCallback(
    (id: string) => {
      history.push(simulatorResultsWithBacktestPath(id));
    },
    [history]
  );

  const onDeleteBacktest = useCallback(async (id: string) => {
    if (!window.confirm("Delete this simulation permanently? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await deleteBacktest(id);
      setRows((prev) => prev.filter((b) => b.id !== id));
      setRowSelection((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Simulation deleted");
    } catch (err) {
      presentMutationError(err, "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }, []);

  const selectedSimulationIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => id),
    [rowSelection],
  );

  const onBulkDeleteBacktests = useCallback(async () => {
    if (selectedSimulationIds.length === 0) return;
    const count = selectedSimulationIds.length;
    if (
      !window.confirm(
        `Delete ${count} simulation${count === 1 ? "" : "s"} permanently? This cannot be undone.`,
      )
    ) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      await Promise.all(selectedSimulationIds.map((id) => deleteBacktest(id)));
      const idSet = new Set(selectedSimulationIds);
      setRows((prev) => prev.filter((b) => !idSet.has(b.id)));
      setRowSelection({});
      setSelectAllFromHeader(false);
      toast.success(`Deleted ${count} simulation${count === 1 ? "" : "s"}`);
    } catch (err) {
      presentMutationError(err, "Delete failed");
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedSimulationIds]);

  const selectedToolbarActions =
    selectedSimulationIds.length > 0 ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-font="primary"
        data-trade-log-action="delete"
        className="w-full shrink-0 !border-red-500 !text-red-500 sm:w-auto"
        onClick={() => void onBulkDeleteBacktests()}
        disabled={isBulkDeleting || deletingId != null}
      >
        <span>Delete Selected</span>
      </Button>
    ) : null;

  const openNewSimulation = useCallback(() => {
    history.push(SIMULATOR_NEW_PATH);
  }, [history]);

  const openReport = useCallback(
    (reportId: string, reportDisplayTitle: string) => {
      history.push({
        pathname: `/reports/${encodeURIComponent(reportId)}`,
        state: { reportDisplayTitle },
      });
    },
    [history],
  );

  const showInitialLoading = listLoading;

  const hasActiveSearch = searchQueryTrimmed.length > 0;
  const hasActiveOriginFilter = originFilter !== "all";
  const emptyIsFilterMismatch = rows.length > 0 && filteredRows.length === 0;
  const emptyStateCopy = useMemo(() => {
    if (!emptyIsFilterMismatch) {
      return {
        title: "No simulations yet",
        description: "Run a new simulation to save and review results here.",
      };
    }
    if (hasActiveSearch && hasActiveOriginFilter) {
      return {
        title: "No simulations match your filters",
        description: "Try a different search term or origin filter.",
      };
    }
    if (hasActiveSearch) {
      return {
        title: "No simulations match your search",
        description: "Try a different search term.",
      };
    }
    return {
      title: "No simulations match this filter",
      description: "Try a different origin filter to see more runs.",
    };
  }, [emptyIsFilterMismatch, hasActiveOriginFilter, hasActiveSearch]);

  const simulatorHelpHeaderActions = useMemo(
    () => <NeedHelpButton href="https://help.tesseratrades.com/simulator" />,
    []
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(simulatorHelpHeaderActions);
    return () => setInsetHeaderActions(null);
  }, [setInsetHeaderActions, shadcnInset, simulatorHelpHeaderActions]);

  return (
    <Box
      data-simulator-page=""
      display="flex"
      flexDirection="column"
      pt={shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" }}
      pb="24px"
      px={shadcnInset ? 0 : { base: "20px", xl: "40px" }}
      gap="24px"
      w="100%"
      minW={0}
      minH={pageMinHeight}
    >
      {!shadcnInset ? (
        <PageRouteHeadline color="#38BBD1">Simulator</PageRouteHeadline>
      ) : null}

      {showInitialLoading ? (
        <Flex flex="1" minH={0} w="100%" align="center" justify="center">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title="Loading simulations"
            description="Please wait while we load your simulations."
          />
        </Flex>
      ) : (
        <div data-simulator-list-card="" className={SIMULATOR_LIST_CARD_CLASS}>
          <SimulatorListActionBar
            originFilter={originFilter}
            onOriginFilterChange={setOriginFilter}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onRunNewSimulation={openNewSimulation}
            bulkActions={selectedToolbarActions}
          />

          {error ? (
            <SimulatorAlert tone="error" className="w-full">
              {error}
            </SimulatorAlert>
          ) : !listLoading && filteredRows.length === 0 ? (
            <Flex flex="1" minH="280px" w="100%" align="center" justify="center">
              <StatisticsEmptyState
                variant="no-data"
                fillParent
                title={emptyStateCopy.title}
                description={emptyStateCopy.description}
              />
            </Flex>
          ) : (
            <SimulatorListTable
              rows={filteredRows}
              reportTitlesById={reportTitlesById}
              reportTypesById={reportTypesById}
              deletingId={deletingId}
              isBulkDeleting={isBulkDeleting}
              onOpenRun={openRun}
              onOpenReport={openReport}
              onDelete={(id) => void onDeleteBacktest(id)}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              selectAllFromHeader={selectAllFromHeader}
              onSelectAllFromHeaderChange={setSelectAllFromHeader}
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          )}
        </div>
      )}
    </Box>
  );
}
